<template>
    <full-modal @close="$emit('close')">
        <div slot="title">Unit settings</div>
        <div slot="body">
            <p class="f-14-asphalt f-sbold pb-4">Information</p>
            <div class="w-100 pb-30">
                <form-input 
                    label="Unit name"
                    :class="[{ 'form-input-danger' : errors.has('unit_name') }]"
                >
                    <input 
                        type="text" 
                        name="unit_name" 
                        class="form-input-field"
                        placeholder="Unit name"
                        v-input-style="unit.name"
                        v-model="unit.name"
                        v-validate="{ required: true, regex: titleRegex }"
                        id="unit_name"
                    />
                </form-input>
            </div>
            <p class="f-14-asphalt f-sbold pb-4">Address</p>
            <div class="w-100 pb-30">
                <address-box                     
                    type="address"
                    :key="'address'"
                    :existing="unitAddress"
                    :apartment="true"
                    @address="getMainAddress"
                >
                    <div 
                        class="w-100 mb-4" 
                        slot="apartment"
                    >
                        <form-input 
                            label="Unit"
                            :class="[{ 'form-input-danger' : errors.has('apartment') }]"
                        >
                            <input 
                                type="text" 
                                name="apartment" 
                                id="apartment"
                                class="form-input-field"
                                placeholder="Unit"
                                v-input-style="unit.apartment"
                                v-model="unit.apartment"
                                v-validate="'max:50'"
                            />
                        </form-input>
                    </div>
                </address-box>
            </div>
            <p class="f-14-black f-sbold">Want to delete this unit?</p>
            <div
                v-if="unit.jobs_count"
                class="f-13-grey f-sbold mt-4"
            >
                <warning-icon
                    size="13"
                    class="i-red mr-1"
                />
                Unit has active Jobs. Delete Jobs to deleting Unit
            </div>
            <template v-else>
                <p class="f-13-grey">
                    By clicking Delete unit you completely erase information on the unit.
                </p>
                <template v-if="isDeleteConfirmShow">
                    <form-input-textarea 
                        label="Reason"
                        v-input-counter="500"
                    >
                        <textarea
                            :class="[
                                'form-input-field',
                                { 'form-input-textarea-danger' : errors.has('deleting_reason') }
                            ]"
                            rows="1"
                            name="deleting_reason"
                            id="deleting_reason"
                            placeholder="Reason"
                            v-model="deletingReason"
                            v-input-style="deletingReason"
                            maxlength="500"
                            v-validate="'required|min:3|max:500'"
                            v-resize-textarea
                        />
                    </form-input-textarea>
                    <div class="row-end">
                        <button
                            class="add-button mr-4"
                            @click="isDeleteConfirmShow = false"
                        >
                            Cancel
                        </button>
                        <button
                            class="red-button"
                            @click="deleteProperty"
                            :disabled="sending"
                        >
                            Yes, delete
                        </button>
                    </div>
                </template>
                <button
                    v-else
                    class="transparent-button s-align-4 mt-4"
                    @click="showDeleteConfirm"
                >
                    <trash-icon class="i-red mr-1"/>
                    <span class="f-13-red f-sbold">Delete unit</span>
                </button>
            </template>
        </div>
        <div slot="footer">
            <button 
                class="add-button"
                @click="$emit('close')"
            >
                Cancel
            </button>
            <button 
                class="primary-button"
                @click="save"
                :disabled="sending"
            >
                Save
            </button>
        </div>
    </full-modal>
</template>

<script>
    import { FullModal, FormInput, FormInputTextarea } from '@/ui';
    import { ArrowIcon, WarningIcon, TrashIcon } from '@/icons';
    import { copyFieldsValueFromObjectToObject, validationErrorNotification } from '@/helpers';
    import cloneDeep from 'lodash/cloneDeep';
    import { COMMERCIAL_PHONE_TYPES, NOT_ALLOWED_CHARACTERS } from '@/constants';
    import AddressBox from '@/components/AddressBox.vue';
    import api from '@/headers.js';

    export default {
        name: 'EditCommercialUnitModal',
        components: {
            FullModal, 
            FormInput,
            FormInputTextarea,
            ArrowIcon,
            WarningIcon,
            TrashIcon,
            AddressBox
        },
        props: {
            unit: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                unitAddress: {
                    address: '',
                    street: '',
                    city: '',
                    state: '',
                    zip: '',
                    lat: null,
                    lng: null
                },
                phoneTypes: COMMERCIAL_PHONE_TYPES,
                sending: false,
                isDeleteConfirmShow: false,
                deletingReason: '',
                titleRegex: NOT_ALLOWED_CHARACTERS
            }
        },
        methods: {
            getMainAddress(data) {
                this.propertyAddress = data;
                copyFieldsValueFromObjectToObject(data, this.unit);
            },
            save() {
                this.$children.forEach(vm => {
                    vm.$validator.validateAll();
                });

                this.$validator.validateAll().then(result => {
                    if (!result) {
                        const context = this;
                        validationErrorNotification(this.$validator, context);
                        return;
                    }

                    this.sending = true;
                    api.put(`/commercial/units/${this.unit.id}`, this.unit)
                        .then(response => {
                            this.notifySuccess('Unit is updated!');

                            this.unit.full_address = response.data.data.full_address;
                            this.$emit('update', this.unit);
                        })
                        .catch((error) => {
                            this.notifyRequestErrors(error);
                        })
                        .finally(() => {
                            this.sending = false;
                        });
                });
            },
            showDeleteConfirm() {
                this.isDeleteConfirmShow = true;
                this.$nextTick(() => {
                    const input = document.getElementById('deleting_reason');
                    // scroll to textarea
                    input.scrollIntoView({ behavior: 'smooth' });
                    // focus on textarea
                    input.focus({ preventScroll: true });
                });                
            },
            deleteProperty() {
                this.$validator.validate('deleting_reason')
                    .then(result => {
                        if (result) {
                            this.sending = true;
                            api.delete(`/commercial/units/${this.unit.id}?reason=${this.deletingReason}`)
                                .then(() => {
                                    this.notifySuccess('Unit deleted.');
                                    this.$emit('delete', this.unit.id);
                                })
                                .catch((error) => { this.notifyRequestErrors(error) })
                                .finally(() => { this.sending = false });
                        }
                    });
            }
        },
        created() {
            copyFieldsValueFromObjectToObject(this.unit, this.unitAddress);
        }
    }
</script>